import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useLang } from "../context/LanguageContext";
import { X, UploadCloud, Check, Loader2, FileText } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const ALLOWED = ["pdf", "doc", "docx"];
const MAX = 10 * 1024 * 1024;

export default function CareerApplyModal({ open, position, onClose }) {
  const { lang, dir } = useLang();
  const ar = lang === "ar";
  const [form, setForm] = useState({ name: "", email: "", phone: "", linkedin: "", cover: "" });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      setForm({ name: "", email: "", phone: "", linkedin: "", cover: "" });
      setFile(null);
      setSent(false);
      setError("");
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const upd = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const pickFile = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const ext = f.name.split(".").pop().toLowerCase();
    if (!ALLOWED.includes(ext)) {
      setError(ar ? "صيغة الملف يجب أن تكون PDF أو DOC أو DOCX" : "File must be PDF, DOC or DOCX");
      setFile(null);
      return;
    }
    if (f.size > MAX) {
      setError(ar ? "حجم الملف يتجاوز 10 ميغابايت" : "File exceeds the 10MB limit");
      setFile(null);
      return;
    }
    setError("");
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      setError(ar ? "يرجى إرفاق سيرتك الذاتية" : "Please attach your resume");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("position", position || "");
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("phone", form.phone);
      fd.append("linkedin", form.linkedin);
      fd.append("cover", form.cover);
      fd.append("lang", lang);
      fd.append("resume", file);
      const res = await fetch(`${API}/api/applications`, { method: "POST", body: fd });
      if (!res.ok) throw new Error("failed");
      setSent(true);
    } catch (err) {
      setError(ar ? "تعذّر إرسال الطلب. حاول مرة أخرى." : "Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl bg-white border border-[rgba(10,31,60,0.14)] px-4 py-3 text-sm text-[color:var(--rq-navy)] placeholder:text-[color:var(--rq-muted)] outline-none transition-colors duration-300 focus:border-[color:var(--rq-green)]";

  return createPortal(
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4" dir={dir} data-testid="apply-modal">
      <div className="absolute inset-0 bg-[rgba(6,20,12,0.6)] backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl border border-[rgba(10,31,60,0.1)]">
        <button
          onClick={onClose}
          className="absolute top-4 ltr:right-4 rtl:left-4 grid place-items-center h-9 w-9 rounded-full bg-[rgba(10,31,60,0.06)] text-[color:var(--rq-navy)] hover:bg-[rgba(14,122,58,0.12)] transition-colors"
          aria-label="close"
          data-testid="apply-modal-close"
        >
          <X size={18} />
        </button>
        <div className="p-6 md:p-8">
          {sent ? (
            <div className="text-center py-10" data-testid="apply-success">
              <div className="mx-auto grid place-items-center h-16 w-16 rounded-full bg-[rgba(14,122,58,0.12)] mb-5">
                <Check size={32} className="text-[color:var(--rq-green)]" />
              </div>
              <h3 className="font-display text-xl font-bold text-[color:var(--rq-navy)]">{ar ? "تم إرسال طلبك!" : "Application submitted!"}</h3>
              <p className="mt-3 text-sm text-[color:var(--rq-muted)]">
                {ar ? "شكرًا لاهتمامك. سيتواصل معك فريقنا قريبًا." : "Thanks for your interest. Our team will be in touch soon."}
              </p>
              <button onClick={onClose} className="rq-glow-btn mt-7 px-7 py-3" data-testid="apply-done-btn">{ar ? "تم" : "Done"}</button>
            </div>
          ) : (
            <>
              <span className="rq-tag" style={{ color: "var(--rq-green)" }}>{ar ? "التقديم على وظيفة" : "Apply for"}</span>
              <h3 className="font-display mt-2 text-xl md:text-2xl font-bold text-[color:var(--rq-navy)]" data-testid="apply-position-title">{position}</h3>
              <form onSubmit={submit} className="mt-5 grid gap-3.5">
                <input required className={inputCls} placeholder={ar ? "الاسم الكامل" : "Full name"} value={form.name} onChange={upd("name")} data-testid="apply-name" />
                <input required type="email" className={inputCls} placeholder={ar ? "البريد الإلكتروني" : "Email"} value={form.email} onChange={upd("email")} data-testid="apply-email" />
                <input className={inputCls} placeholder={ar ? "رقم الهاتف" : "Phone"} value={form.phone} onChange={upd("phone")} data-testid="apply-phone" />
                <input className={inputCls} placeholder={ar ? "رابط لينكدإن (اختياري)" : "LinkedIn URL (optional)"} value={form.linkedin} onChange={upd("linkedin")} data-testid="apply-linkedin" />
                <textarea rows={3} className={`${inputCls} resize-none`} placeholder={ar ? "رسالة تعريفية (اختياري)" : "Cover note (optional)"} value={form.cover} onChange={upd("cover")} data-testid="apply-cover" />
                <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" onChange={pickFile} className="hidden" data-testid="apply-resume-input" />
                <button
                  type="button"
                  onClick={() => fileRef.current && fileRef.current.click()}
                  className="flex items-center gap-3 rounded-xl border border-dashed border-[rgba(14,122,58,0.4)] bg-[rgba(14,122,58,0.04)] px-4 py-3.5 text-start transition-colors hover:bg-[rgba(14,122,58,0.08)]"
                  data-testid="apply-resume-btn"
                >
                  <span className="grid place-items-center h-10 w-10 rounded-lg bg-[rgba(14,122,58,0.12)] text-[color:var(--rq-green)] shrink-0">
                    {file ? <FileText size={18} /> : <UploadCloud size={18} />}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-[color:var(--rq-navy)] truncate">
                      {file ? file.name : (ar ? "إرفاق السيرة الذاتية" : "Attach your resume")}
                    </span>
                    <span className="block text-xs text-[color:var(--rq-muted)]">
                      {ar ? "PDF أو DOC أو DOCX — بحد أقصى 10 ميغابايت" : "PDF, DOC or DOCX — up to 10MB"}
                    </span>
                  </span>
                </button>
                {error && <p className="text-sm text-red-500" data-testid="apply-error">{error}</p>}
                <button type="submit" disabled={loading} className="rq-glow-btn mt-1 w-full py-3.5 flex items-center justify-center gap-2 disabled:opacity-60" data-testid="apply-submit">
                  {loading ? (
                    <>{ar ? "جارٍ الإرسال..." : "Sending..."}<Loader2 size={16} className="animate-spin" /></>
                  ) : (
                    ar ? "إرسال الطلب" : "Submit Application"
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
